/**
 * AutomationDashboardCockpit — Cockpit-language frame for /automations.
 *
 * Wraps the original AutomationDashboard with a Cockpit Masthead + KPI strip
 * to guarantee visible cockpit chrome. The interactive rule/reminder/log
 * content is preserved verbatim — no business logic changed.
 */
import { useMemo } from "react";
import {
  CockpitPage, CockpitMasthead, HeroMetric, HeroDivider, CockpitKpiStrip,
} from "@/components/cockpit/CockpitShell";
import {
  DEFAULT_AUTOMATION_RULES,
} from "@/data/automationEngine";
import AutomationDashboard from "./AutomationDashboard";

export default function AutomationDashboardCockpit() {
  // Compute KPIs from DB-first surface defaults (rules + empty runtime logs).
  const stats = useMemo(() => {
    const rules = DEFAULT_AUTOMATION_RULES;
    const executions: { success: boolean }[] = [];
    const reminders: { status: string }[] = [];
    const success = executions.filter((e) => e.success).length;
    return {
      totalRules: rules.filter((r) => r.status !== "archived").length,
      activeRules: rules.filter((r) => r.enabled && r.status === "active").length,
      executions: executions.length,
      successRate: executions.length > 0 ? Math.round((success / executions.length) * 100) : 100,
      pendingReminders: reminders.filter((r) => r.status === "pending").length,
    };
  }, []);

  return (
    <CockpitPage>
      <CockpitMasthead
        eyebrow="Automations & Intelligence · Engine"
        title="Automation Engine"
        right={
          <>
            <HeroMetric label="Active Rules" value={stats.activeRules} hero />
            <HeroDivider />
            <HeroMetric label="Executions" value={stats.executions} />
            <HeroDivider />
            <HeroMetric
              label="Success Rate"
              value={`${stats.successRate}%`}
              tone={stats.successRate >= 95 ? "good" : stats.successRate >= 80 ? "warn" : "bad"}
            />
          </>
        }
      />
      <CockpitKpiStrip
        items={[
          { label: "Total Rules", value: stats.totalRules },
          { label: "Active", value: stats.activeRules, tone: "good" },
          { label: "Pending Reminders", value: stats.pendingReminders, tone: stats.pendingReminders > 0 ? "warn" : "default" },
          { label: "Executions Logged", value: stats.executions },
        ]}
      />
      {/* Embedded original interactive surface — unchanged. */}
      <div className="cockpit-surface -mx-6">
        <AutomationDashboard />
      </div>
    </CockpitPage>
  );
}
